/**
 * Keep window scroll when UI re-renders, focus moves, or a modal mounts.
 * Sudden jump from a deep position to ~0 is treated as accidental and restored.
 */

let lastMeaningfulY = 0;
let lastY = 0;
let restoring = false;
let routePathname = '';
let installed = false;
let pinUntil = 0;
let pinTargetY = 0;
let pinRaf = 0;
let mo = null;

function readY() {
  return window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
}

function writeY(y) {
  window.scrollTo(0, y);
  try {
    document.documentElement.scrollTop = y;
    document.body.scrollTop = y;
  } catch (_) {
    /* ignore */
  }
}

function restoreTo(target) {
  if (!target || target < 8 || restoring) return;
  restoring = true;
  writeY(target);
  lastMeaningfulY = target;
  requestAnimationFrame(() => {
    writeY(target);
    requestAnimationFrame(() => {
      writeY(target);
      lastY = readY();
      restoring = false;
    });
  });
}

function onScroll() {
  if (restoring) return;
  const y = readY();
  const prev = lastY;
  lastY = y;

  // Accidental jump to top (focus / remount / portal / modal)
  if (prev > 80 && y < 8 && prev - y > 60) {
    const target =
      pinUntil > Date.now() && pinTargetY > 40
        ? pinTargetY
        : lastMeaningfulY > 40
          ? lastMeaningfulY
          : prev;
    restoreTo(target);
    return;
  }

  if (y > 8) {
    lastMeaningfulY = y;
    if (pinUntil > Date.now()) pinTargetY = y;
  }
}

function onFocusIn() {
  // Only fight focus-driven jumps while a pin window is active
  if (restoring || Date.now() >= pinUntil) return;
  const y = readY();
  if (pinTargetY > 40 && y < 8) restoreTo(pinTargetY);
}

function checkPinnedJump() {
  if (restoring || Date.now() >= pinUntil) return;
  const y = readY();
  if (pinTargetY > 40 && y < 8) restoreTo(pinTargetY);
  else if (y > 8) lastMeaningfulY = y;
  lastY = readY();
}

function runPinLoop() {
  cancelAnimationFrame(pinRaf);
  const tick = () => {
    checkPinnedJump();
    if (Date.now() < pinUntil) {
      pinRaf = requestAnimationFrame(tick);
    } else {
      pinRaf = 0;
    }
  };
  pinRaf = requestAnimationFrame(tick);
}

/**
 * Call once from Layout.
 */
export function installWindowScrollGuard() {
  if (installed || typeof window === 'undefined') return () => {};
  installed = true;
  try {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  } catch (_) {
    /* ignore */
  }
  lastY = readY();
  lastMeaningfulY = lastY;
  window.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('focusin', onFocusIn, true);
  try {
    mo = new MutationObserver(() => {
      if (Date.now() < pinUntil) {
        requestAnimationFrame(checkPinnedJump);
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  } catch (_) {
    mo = null;
  }
  return () => {
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('focusin', onFocusIn, true);
    if (mo) {
      mo.disconnect();
      mo = null;
    }
    cancelAnimationFrame(pinRaf);
    installed = false;
  };
}

/**
 * Reset scroll only when the path changes — not on ?tab= / filter query tweaks.
 */
export function setScrollRouteKey(pathnameOrKey, search = undefined) {
  const path =
    search === undefined
      ? String(pathnameOrKey || '').split('?')[0]
      : String(pathnameOrKey || '');
  if (path === routePathname) return;
  routePathname = path;
  lastMeaningfulY = 0;
  lastY = 0;
  pinUntil = 0;
  pinTargetY = 0;
  restoring = false;
  writeY(0);
}

/** Snapshot current scroll (call before opening modal / mutating UI). */
export function captureWindowScroll() {
  const y = readY();
  if (y > 8) {
    lastMeaningfulY = y;
    pinTargetY = y;
  }
  lastY = y;
  return y;
}

/**
 * Hold scroll at the captured (or current meaningful) Y for a short window
 * while React commits modals / refetches. Prefer this over a one-shot restore.
 */
export function pinWindowScroll(y, durationMs = 900) {
  const target = typeof y === 'number' && y > 8 ? y : lastMeaningfulY;
  if (!target || target < 8) return target || 0;
  lastMeaningfulY = target;
  pinTargetY = target;
  pinUntil = Date.now() + Math.max(200, durationMs || 900);
  restoreTo(target);
  runPinLoop();
  const delays = [0, 50, 120, 250, 450, 700];
  delays.forEach((ms) => {
    setTimeout(() => {
      if (Date.now() > pinUntil + 50) return;
      restoreTo(pinTargetY || target);
    }, ms);
  });
  return target;
}

/** Restore a captured Y (and reinforce after focus/layout). */
export function restoreWindowScroll(y, opts = {}) {
  const target = typeof y === 'number' ? y : lastMeaningfulY;
  if (!target || target < 8) return;
  const duration = opts.durationMs ?? 900;
  pinWindowScroll(target, duration);
}

/**
 * onMouseDown on action buttons: avoid focus scrolling the page to the control.
 */
export function preventFocusScroll(e) {
  if (e && typeof e.preventDefault === 'function') e.preventDefault();
}
